import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { Guid } from 'guid-typescript';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';
import { DiscardGuard } from '../../../shared/utils/discard-guard';
import { emailValidator } from '../../../common/email-validator.directive';
import { nameValidator } from '../../../common/name-validator.directive';
import { namePartLengthValidator, personNameValidator, splitFullName } from '../../../common/person-name';
import { numberValidator } from '../../../common/number-validator.directive';
import { phoneValidator } from '../../../common/phone-validator.directive';
import { AuthService } from '../../../core/auth/auth.service';
import { BusinessAccountService } from '../../../shared/apis/business-account/api/businessAccount.service';
import { BusinessAccountCreateCommand } from '../../../shared/apis/business-account/model/businessAccountCreateCommand';
import { BusinessAccountDto } from '../../../shared/apis/business-account/model/businessAccountDto';
import { ContactService } from '../../../shared/apis/contact/api/contact.service';
import { ContactCreateCommand } from '../../../shared/apis/contact/model/contactCreateCommand';
import { ContactDto } from '../../../shared/apis/contact/model/contactDto';
import { ContactUpdateCommand } from '../../../shared/apis/contact/model/contactUpdateCommand';
import { BloodGroup } from '../../../shared/common/enums/bloodGroup';
import { ContactType } from '../../../shared/common/enums/contactType';
import { Gender } from '../../../shared/common/enums/gender';
import { Salutation } from '../../../shared/common/enums/salutation';
import { MobileNumberDisplayPipe } from '../../../shared/pipes/mobile-number-display.pipe';
import { ContactsService } from '../contacts.service';
import { Country } from '../contacts.types';

@Component({
    selector: 'contact-add',
    templateUrl: './contact-add.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ContactAddComponent implements OnInit, OnDestroy {
    @Input() contactForm: UntypedFormGroup;
    @Input() routeParam: string;
    @Input() contact$: ContactDto;
    @Input() displayAvatar: boolean;
    @Input() allowMultipleEntries: boolean;
    @Input() navigateAfterSave: boolean;
    @Input() displayInDialog: boolean;
    @Output() editModeToggle = new EventEmitter<boolean>();
    @ViewChild('avatarFileInput') private _avatarFileInput: ElementRef;

    countries: Country[];
    /** DESIGN-RULES R7: the IS add modal's Cancel asks "Discard your changes?" when anything was typed. */
    discard = new DiscardGuard();
    /**
     * Inside Sales lightweight add path: the dedicated full-page add route
     * (displayInDialog === false). Drives the 4-field reshaped modal
     * (Full name · Role · Email · Link to account) per the IS prototype
     * (GAP-MEETUX-CONTACT-002). The meeting contact-add-dialog
     * (displayInDialog === true) keeps the existing markup/behaviour.
     */
    get isIsAddForm(): boolean {
        return this.routeParam === 'newContact' && !this.displayInDialog;
    }
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        private _snackBar: MatSnackBar,
        private _contactService: ContactService,
        private _contactsService: ContactsService,
        private _authService: AuthService,
        private route: ActivatedRoute,
        private _mobileNumberDisplayPipe: MobileNumberDisplayPipe,
        private _businessAccountService: BusinessAccountService
    ) {
      this._contactsService.getCountries().subscribe();
    }

    ngOnInit(): void {
        // Get the country telephone codes
        this._contactsService.countries$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((codes: Country[]) => {
                this.countries = codes;
            });

        // The Inside Sales add path builds its own reduced 4-field form;
        // the dialog/edit paths keep the full CRM form (GAP-MEETUX-CONTACT-002).
        if (this.isIsAddForm) {
            this.initializeIsAddForm();
            return;
        }

        this.initializeContactForm();

        if (this.routeParam === 'newContact') {
            this.contact$ = {};
            this.contactForm.reset();
            this.contactForm.get('isLead').setValue(false);
            this.contactForm.get('gender').setValue(Gender.Male);
            this.prepareContactFormsForAdd();
        } else {
            // Patch values to the form
            this.contactForm.patchValue(this.contact$);
            this.prepareContactFormsForEdit();
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.contact$ = {};
    }

    toggleEditMode(isEditMode: boolean): void {
        this.editModeToggle.emit(isEditMode);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * Get country info by iso code
     *
     * @param iso
     */
    getCountryByIso(iso: string): Country {
        return this.countries?.find(country => country.iso === iso);
    }

    /**
     * Get country info by name
     *
     * @param iso
     */
    getCountryByName(name: string): Country {
        return this.countries?.find(country => country.name === name);
    }

    /**
     * Build the Inside Sales lightweight add form per the IS prototype
     * (contacts-is.html add-contact modal): Full name · Role · Email ·
     * Link to account. Replaces the full CRM editor on the IS add path only
     * (GAP-MEETUX-CONTACT-002). `linkToAccount` is render-only — it is not
     * submitted (GAP-MEETUX-CONTACT-008, depends on CRM sync).
     */
    initializeIsAddForm(): void {
        this.contact$ = {};
        this.contactForm = this._formBuilder.group({
            fullName: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(100),
                    personNameValidator(),
                    namePartLengthValidator(),
                ],
            ],
            role: ['', [Validators.maxLength(50)]],
            email: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(250),
                    emailValidator(),
                ],
            ],
            // Render-only per the prototype; not sent to the backend in this session.
            linkToAccount: [''],
        });
        this.discard.reset(this.contactForm.getRawValue());
    }

    initializeContactForm(): void {
        try {
            // Create the contact form
            this.contactForm = this._formBuilder.group({
                id: [''],
                type: [''],
                salutation: [''],
                firstName: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(1),
                        Validators.maxLength(50),
                        nameValidator(),
                    ],
                ],
                lastName: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(1),
                        Validators.maxLength(50),
                        nameValidator(),
                    ],
                ],
                organization: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(1),
                        Validators.maxLength(100),
                    ],
                ],
                designation: [
                    '',
                    [Validators.minLength(1), Validators.maxLength(50)],
                ],
                department: [
                    '',
                    [Validators.minLength(1), Validators.maxLength(50)],
                ],
                gender: [Gender.Male, Validators.required],
                isLead: [false],
                avatarUri: [null],
                emails: this._formBuilder.array([]),
                phoneNumbers: this._formBuilder.array([]),
                mobileNumbers: this._formBuilder.array([]),
                // company: ['', [Validators.required,
                // Validators.minLength(1),
                // Validators.maxLength(250),]],
                birthDate: [null],
                addresses: this._formBuilder.array([]),
                note: [null],
                websites: this._formBuilder.array([])
            });
        } catch (ex) {
            console.error('Exception in initializeContactForm()');
            console.error(ex);
        }
    }

    prepareContactFormsForAdd(): void {
        try {
            // Clear the emails and phoneNumbers form arrays
            (this.contactForm.get('emails') as UntypedFormArray).clear();
            (this.contactForm.get('phoneNumbers') as UntypedFormArray).clear();
            (this.contactForm.get('mobileNumbers') as UntypedFormArray).clear();
            (this.contactForm.get('addresses') as UntypedFormArray).clear();
            (this.contactForm.get('websites') as UntypedFormArray).clear();

            // Add the email form groups to the emails form array
            (this.contactForm.get('emails') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.required,
                            Validators.minLength(1),
                            Validators.maxLength(250),
                            emailValidator(),
                        ],
                    ],
                    label: ['Email'],
                })
            );

            // Add the website form groups to the websites form array
            (this.contactForm.get('websites') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [''],
                    label: ['Web Site'],
                })
            );

            // Add the email form groups to the emails form array
            (this.contactForm.get('addresses') as UntypedFormArray).push(
                this._formBuilder.group({
                    street: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // area: [
                    //     '',
                    //     [
                    //         Validators.required,
                    //         Validators.minLength(1),
                    //         Validators.maxLength(250),
                    //     ],
                    // ],
                    city: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    pincode: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(6)],
                    ],
                    landmark: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // district: ['', [Validators.required, nameValidator()]],
                    // state: ['', [Validators.required, nameValidator()]],
                    country: ['', [Validators.required, nameValidator()]],
                })
            );

            // Add the phone numbers form groups to the phone numbers form array
            (this.contactForm.get('phoneNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Phone'],
                    country: ['us'],
                })
            );

            // Add the phone numbers form groups to the phone numbers form array
            (this.contactForm.get('mobileNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Mobile'],
                    country: ['us'],
                })
            );
        } catch (ex) {
            // Surface genuine init failures instead of swallowing them
            // (GAP-MEETUX-CONTACT-003).
            console.error('Exception in prepareContactFormsForAdd()');
            console.error(ex);
            this._snackBar.open(
                'Could not initialise the contact form. Please contact system admin!',
                'Close',
                { duration: 3000 }
            );
        }
    }

    prepareContactFormsForEdit(): void {
        // Clear the emails and phoneNumbers form arrays
        (this.contactForm.get('emails') as UntypedFormArray).clear();
        (this.contactForm.get('phoneNumbers') as UntypedFormArray).clear();
        (this.contactForm.get('mobileNumbers') as UntypedFormArray).clear();
        (this.contactForm.get('addresses') as UntypedFormArray).clear();
        (this.contactForm.get('websites') as UntypedFormArray).clear();

        // Setup the emails form array
        if (this.contact$.emails && this.contact$.emails.length > 0) {
            // Iterate through them
            this.contact$.emails.forEach((email) => {
                // Add the email form groups to the emails form array
                (this.contactForm.get('emails') as UntypedFormArray).push(
                    this._formBuilder.group({
                        value: [
                            email.value,
                            [
                                Validators.required,
                                Validators.minLength(1),
                                Validators.maxLength(250),
                                emailValidator(),
                            ],
                        ],
                        label: [email.label],
                    })
                );
            });
        } else {
            // Create an email form group
            (this.contactForm.get('emails') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.required,
                            Validators.minLength(1),
                            Validators.maxLength(250),
                            emailValidator(),
                        ],
                    ],
                    label: ['Email'],
                })
            );
        }

        // Setup the websites form array
        if (this.contact$.websites && this.contact$.websites.length > 0) {
            // Iterate through them
            this.contact$.websites.forEach((website) => {
                // Add the website form groups to the websites form array
                (this.contactForm.get('websites') as UntypedFormArray).push(
                    this._formBuilder.group({
                        value: [website.value],
                        label: [website.label],
                    })
                );
            });
        } else {
            // Create website form group
            (this.contactForm.get('websites') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [''],
                    label: ['Web Site'],
                })
            );
        }

        // Setup the addresses form array
        if (this.contact$.addresses && this.contact$.addresses.length > 0) {
            // Iterate through them
            this.contact$.addresses.forEach((address) => {
                // Add the email form groups to the emails form array
                (this.contactForm.get('addresses') as UntypedFormArray).push(
                    this._formBuilder.group({
                        street: [address.street],
                        area: [address.area],
                        city: [address.city],
                        // pincode: [address.pincode],
                        landmark: [address.landmark],
                        // district: [address.district],
                        // state: [address.state],
                        country: [this.getCountryByName(address.country).iso, [Validators.required]],
                    })
                );
            });
        } else {
            // Create an email form group
            (this.contactForm.get('addresses') as UntypedFormArray).push(
                this._formBuilder.group({
                    street: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // area: [
                    //     '',
                    //     [
                    //         Validators.required,
                    //         Validators.minLength(1),
                    //         Validators.maxLength(250),
                    //     ],
                    // ],
                    city: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    pincode: [
                        '',
                        [
                            Validators.minLength(1),
                            Validators.maxLength(6),
                            numberValidator(),
                        ],
                    ],
                    landmark: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // district: ['', [Validators.required, nameValidator()]],
                    // state: ['', [Validators.required, nameValidator()]],
                    country: ['', [Validators.required, nameValidator()]],
                })
            );
        }

        // Setup the phone numbers form array
        if (
            this.contact$.phoneNumbers &&
            this.contact$.phoneNumbers.length > 0
        ) {
            // Iterate through them
            this.contact$.phoneNumbers.forEach((phoneNumber) => {
                // Add the phone numbers form groups to the phone numbers form array
                (this.contactForm.get('phoneNumbers') as UntypedFormArray).push(
                    this._formBuilder.group({
                        value: [
                            this._mobileNumberDisplayPipe.transform(phoneNumber.value,
                                this.getCountryByIso(phoneNumber.country).code),
                            [
                                Validators.minLength(1),
                                phoneValidator(),
                            ],
                        ],
                        label: [phoneNumber.label],
                        country: [phoneNumber.country],
                    })
                );
            });
        } else {
            // Create a phone number form group
            (this.contactForm.get('phoneNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Phone'],
                    country: ['us'],
                })
            );
        }

        // Setup the phone numbers form array
        if (
            this.contact$.mobileNumbers &&
            this.contact$.mobileNumbers.length > 0
        ) {
            // Iterate through them
            this.contact$.mobileNumbers.forEach((mobileNumber) => {
                // Add the phone numbers form groups to the phone numbers form array
                (
                    this.contactForm.get('mobileNumbers') as UntypedFormArray
                ).push(
                    this._formBuilder.group({
                        value: [
                            this._mobileNumberDisplayPipe.transform(mobileNumber.value,
                                this.getCountryByIso(mobileNumber.country).code),
                            [
                                Validators.minLength(1),
                                Validators.maxLength(10),
                                phoneValidator(),
                            ],
                        ],
                        label: [mobileNumber.label],
                        country: [mobileNumber.country],
                    })
                );
            });
        } else {
            // Create a phone number form group
            (this.contactForm.get('mobileNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Mobile'],
                    country: ['us'],
                })
            );
        }
    }

    /**
     * Upload avatar
     *
     * @param fileList
     */
    uploadAvatar(fileList: FileList): void {
        // Return if canceled
        if (!fileList.length) {
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png'];
        const file = fileList[0];

        // Return if the file is not allowed
        if (!allowedTypes.includes(file.type)) {
            return;
        }

        // Upload the avatar
        // this._contactsService.uploadAvatar(this.contact.id, file).subscribe();
    }

    /**
     * Remove the avatar
     */
    removeAvatar(): void {
        // Get the form control for 'avatar'
        const avatarFormControl = this.contactForm.get('avatar');

        // Set the avatar as null
        avatarFormControl.setValue(null);

        // Set the file input value as null
        this._avatarFileInput.nativeElement.value = null;

        // Update the contact
        // this.contact.avatar = null;
    }

    /**
     * Add the email field
     */
    addEmailField(): void {
        // Create an empty email form group
        const emailFormGroup = this._formBuilder.group({
            value: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(250),
                    emailValidator(),
                ],
            ],
            label: ['Email'],
        });

        // Add the email form group to the emails form array
        (this.contactForm.get('emails') as UntypedFormArray).push(
            emailFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove the email field
     *
     * @param index
     */
    removeEmailField(index: number): void {
        // Get form array for emails
        const emailsFormArray = this.contactForm.get(
            'emails'
        ) as UntypedFormArray;

        // Remove the email field
        emailsFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Add an empty phone number field
     */
    addPhoneNumberField(): void {
        // Create an empty phone number form group
        const phoneNumberFormGroup = this._formBuilder.group({
            value: [
                '',
                [
                    Validators.minLength(1),
                    phoneValidator(),
                ],
            ],
            label: ['Phone'],
            country: ['us'],
        });

        // Add the phone number form group to the phoneNumbers form array
        (this.contactForm.get('phoneNumbers') as UntypedFormArray).push(
            phoneNumberFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Update the contact
     */
    updateContact(): void {
        if (this.contactForm.invalid) {
            return;
        }

        // Inside Sales lightweight add path uses its own reduced form shape.
        if (this.isIsAddForm) {
            this.saveIsContact();
            return;
        }

        // Get the contact object
        const contact = this.contactForm.getRawValue() as ContactDto;

        // Go through the contact object and clear empty values
        contact.emails = contact.emails.filter(email => email.value);

        contact.phoneNumbers = contact.phoneNumbers.filter(
            phoneNumber => phoneNumber.value
        );

        contact.mobileNumbers = contact.mobileNumbers.filter(
            mobileNumber => mobileNumber.value
        );

        contact.addresses = contact.addresses.filter(
            address => address.country
        );

        contact.addresses.forEach((address) => {
            address.country = this.getCountryByIso(address.country).name;
        });

        contact.websites = contact.websites.filter(website => website.value);

        contact.salutation = Salutation.NA;
        contact.bloodGroup = BloodGroup.NA;

        if (
            this.contactForm.get('type').value !== undefined &&
            this.contactForm.get('type').value !== null &&
            this.contactForm.get('type').value !== ''
        ) {
            contact.type = this.contactForm.get('type').value;
        } else {
            contact.type = ContactType.NA;
        }

        contact.isLead = this.contactForm.get('isLead').value;

        if (this.routeParam === 'newContact') {
            const contactCommand = new ContactCreateCommand();
            contactCommand.contactDetails = contact;
            contactCommand.contactDetails.id = Guid.create().toString();
            this._contactService
                .contactPost(
                    this._authService.selectedTenant.id,
                    contactCommand,
                    Guid.create().toString()
                )
                .subscribe(async (data) => {
                    if (contact.isLead) {
                        await this.createBusinessAccount(contact);
                    }

                    // Feedback on the contact save itself, not only the
                    // side-effect account creation (GAP-MEETUX-CONTACT-004).
                    this._snackBar.open('Contact added successfully', 'Close', {
                        duration: 3000,
                    });

                    this.toggleEditMode(false);
                    // this.location.back();
                    if (this.navigateAfterSave) {
                      this._router.navigate(['../'], { relativeTo: this.route });
                    }
                }, (error) => {
                    // A failed POST now shows feedback and leaves the form
                    // editable (GAP-MEETUX-CONTACT-006).
                    console.error('Exception in contactPost()');
                    console.error(error);
                    this._snackBar.open(
                        'Could not add the contact. Please try again.',
                        'Close',
                        { duration: 3000 }
                    );
                });
        } else {
            // Update the contact on the server
            const contactCommand = new ContactUpdateCommand();
            contactCommand.contactDetails = contact;

            this._contactService
                .contactPut(this._authService.selectedTenant.id, contactCommand)
                .subscribe((data) => {
                    this.toggleEditMode(false);
                    // this.location.back();
                    if (this.navigateAfterSave) {
                      this._router.navigate(['../'], { relativeTo: this.route });
                    }
                });
        }
    }

    /**
     * Cancel / dismiss the Inside Sales add modal: close the form and return to
     * the contacts list (Cancel routes here; R7: the backdrop and Escape no longer dismiss it).
     */
    cancelIsAdd(): void {
        this.discard.request(this.contactForm.getRawValue(), () => this.closeIsAdd());
    }

    private closeIsAdd(): void {
        this.toggleEditMode(false);
        if (this.navigateAfterSave) {
            this._router.navigate(['../'], { relativeTo: this.route });
        }
    }

    /**
     * Save a contact from the Inside Sales lightweight add form
     * (GAP-MEETUX-CONTACT-002/-004/-006). Maps the 4-field prototype form to
     * ContactDto without touching the ContactCreateCommand contract:
     *   - Full name        -> firstName (first token) + lastName (remainder)
     *   - Role             -> designation
     *   - Email            -> emails[0].value
     *   - Link to account  -> organization
     * The backend ContactDtoValidator requires Organization (NotEmpty), so the
     * prototype's "Link to account" feeds it; when blank we default to
     * 'Unassigned' so the POST still validates. This is a deliberate, scoped use
     * of the field — NOT the live CRM/Account lookup, which stays deferred
     * (GAP-MEETUX-CONTACT-008). isLead is forced false (no BusinessAccount
     * write-back on this path — AR-07 / GAP-MEETUX-CONTACT-005).
     */
    saveIsContact(): void {
        const fullName: string = (this.contactForm.get('fullName').value || '').trim();
        const role: string = (this.contactForm.get('role').value || '').trim();
        const email: string = (this.contactForm.get('email').value || '').trim();
        const linkToAccount: string = (this.contactForm.get('linkToAccount').value || '').trim();

        // Split "Full name" into first/last. First token is the first name;
        // the remainder is the last name (fallback '-' so the record stays valid).
        const { firstName, lastName } = splitFullName(fullName);

        const contact = new ContactDto();
        contact.id = Guid.create().toString();
        contact.firstName = firstName;
        contact.lastName = lastName;
        contact.designation = role || null;
        // Backend requires Organization (NotEmpty); map Link-to-account into it,
        // defaulting when blank so the create POST validates.
        contact.organization = linkToAccount || 'Unassigned';
        contact.emails = [{ value: email, label: 'Email' }];
        contact.type = ContactType.NA;
        contact.salutation = Salutation.NA;
        contact.bloodGroup = BloodGroup.NA;
        // No CRM write-back on the manual IS add path (AR-07 / GAP-MEETUX-CONTACT-005).
        contact.isLead = false;

        const contactCommand = new ContactCreateCommand();
        contactCommand.contactDetails = contact;

        this._contactService
            .contactPost(
                this._authService.selectedTenant.id,
                contactCommand,
                Guid.create().toString()
            )
            .subscribe(
                (data) => {
                    // Success feedback on the contact save (GAP-MEETUX-CONTACT-004);
                    // no sync promise - no CRM sync exists (SPEC-web-planner-013 AC-74).
                    this._snackBar.open(
                        'Contact added',
                        'Close',
                        { duration: 3000 }
                    );
                    this.toggleEditMode(false);
                    if (this.navigateAfterSave) {
                        this._router.navigate(['../'], { relativeTo: this.route });
                    }
                },
                (error) => {
                    // Failed POST: show feedback, keep the form editable
                    // (GAP-MEETUX-CONTACT-006).
                    console.error('Exception in saveIsContact()');
                    console.error(error);
                    this._snackBar.open(
                        'Could not add the contact. Please try again.',
                        'Close',
                        { duration: 3000 }
                    );
                }
            );
    }

    async createBusinessAccount(contact: ContactDto): Promise<void> {

        try {
            const command = new BusinessAccountCreateCommand();
            command.businessAccountDetails = new BusinessAccountDto();
            command.businessAccountDetails.tenantId = this._authService.selectedTenant.id;
            command.businessAccountDetails.code = contact.organization;
            command.businessAccountDetails.name = contact.organization;
            command.businessAccountDetails.contacts = [contact];
            // eslint-disable-next-line max-len
            const businessAccountResponse = await lastValueFrom(this._businessAccountService.businessAccountPost(this._authService.selectedTenant.id, command, Guid.create().toString()));
            if (businessAccountResponse.isSuccess) {
                this._snackBar.open('Account created successfully', 'Close',{
                    duration: 3000
                  });
            } else {
                this._snackBar.open('Account creation failed. Please contact system admin!', 'Close',{
                    duration: 3000
                  });
            }
        } catch(ex) {
            console.error('Exception in createBusinessAccount()');
            console.error(ex);
        }

    }

    /**
     * Add the websites field
     */
    addWebsiteField(): void {
        // Create an empty websites form group
        const websitesFormGroup = this._formBuilder.group({
            value: [''],
            label: ['Web Site'],
        });

        // Add the website form group to the websites form array
        (this.contactForm.get('websites') as UntypedFormArray).push(
            websitesFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove the websites field
     *
     * @param index
     */
    removeWebsitesField(index: number): void {
        // Get form array for websites
        const websitesFormArray = this.contactForm.get(
            'websites'
        ) as UntypedFormArray;

        // Remove the email field
        websitesFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Add an empty Mobile number field
     */
    addMobileNumberField(): void {
        // Create an empty mobile number form group
        const mobileNumberFormGroup = this._formBuilder.group({
            value: [
                '',
                [
                    Validators.minLength(1),
                    phoneValidator(),
                ],
            ],
            label: ['Mobile'],
            country: ['us'],
        });

        // Add the phone number form group to the phoneNumbers form array
        (this.contactForm.get('mobileNumbers') as UntypedFormArray).push(
            mobileNumberFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }
    /**
     * Remove the phone number field
     *
     * @param index
     */
    removePhoneNumberField(index: number): void {
        // Get form array for phone numbers
        const phoneNumbersFormArray = this.contactForm.get(
            'phoneNumbers'
        ) as UntypedFormArray;

        // Remove the phone number field
        phoneNumbersFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Add an empty address field
     */
    addAddressesField(): void {
        // Create an empty address form group
        const addressesFormGroup = this._formBuilder.group({
            street: [''],
            // area: [''],
            city: [''],
            pincode: [''],
            landmark: [''],
            // district: [''],
            // state: [''],
            country: ['', [Validators.required]],
        });

        // Add the address form group to the addresses form array
        (this.contactForm.get('addresses') as UntypedFormArray).push(
            addressesFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove the mobile number field
     *
     * @param index
     */
    removeMobileNumberField(index: number): void {
        // Get form array for Mobile numbers
        const mobileNumbersFormArray = this.contactForm.get(
            'mobileNumbers'
        ) as UntypedFormArray;

        // Remove the mobile number field
        mobileNumbersFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    removeAddressesField(index: number): void {
        // Get form array for Mobile numbers
        const addressesNumbersFormArray = this.contactForm.get(
            'addresses'
        ) as UntypedFormArray;

        // Remove the mobile number field
        addressesNumbersFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }
}
